import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { ChatUiContext } from '../ChatContext';
import useAutoScroll from '../hooks/useAutoScroll';
import MessageList from './MessageList';
import Composer from './Composer';
import { UploadTray, useUploads } from './Attachments';
import PageViewer from './PageViewer';
import '../styles/tokens.css';
import '../styles/chat.css';
import '../styles/markdown.css';

function LiveAnnouncer({ messages }) {
  const { t } = React.useContext(ChatUiContext);
  const [text, setText] = useState('');
  const last = messages[messages.length - 1];
  const stage = last && last.role === 'assistant' ? last.stage : null;
  const label = last && last.role === 'assistant' ? last.stageLabel : '';
  const busy = Boolean(last && last.busy);
  const wasBusy = useRef(false);

  useEffect(() => {
    if (busy && stage && label) setText(label);
  }, [busy, stage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (wasBusy.current && !busy && last && last.status !== 'error') setText(t('Answer complete'));
    wasBusy.current = busy;
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className="cv-sr-only" aria-live="polite" role="status">{text}</div>;
}

function LongChatBanner({ info, onNewChat, onDismiss }) {
  const { t } = React.useContext(ChatUiContext);
  return (
    <div className="cv-banner" role="status">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
      </svg>
      <span className="cv-banner__text">
        {(info && info.limit_hint) || t('This chat is getting long. Start a new chat for the best answers.')}
        {info && info.turns ? <span className="cv-banner__meta">{` (${info.turns} ${t('turns')})`}</span> : null}
      </span>
      <button type="button" className="cv-btn cv-btn--small cv-btn--primary" onClick={onNewChat}>{t('New chat')}</button>
      <button type="button" className="cv-icon-btn" aria-label={t('Dismiss')} title={t('Dismiss')} onClick={onDismiss}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

/**
 * The chat section: message list, composer (with the footer controls App passes in), the long-chat banner
 * and one page viewer. All conversation state lives in `chat` (useChatStream), owned by App.
 */
export default function ChatView({
  chat,
  t,
  langOpt,
  question,
  setQuestion,
  onSend,
  onNewChat,
  onVote,
  votes,
  controls,
  emptyState,
  overlay,
  history,
}) {
  const { state, busy } = chat;
  const { messages, conversationId, conversationInfo } = state;
  const [viewer, setViewer] = useState(null);
  const [dismissedLong, setDismissedLong] = useState({});
  const scroll = useAutoScroll(messages);
  const uploads = useUploads();

  const openViewer = useCallback((sources, sourceId) => {
    if (!sources || !sources.length) return;
    setViewer({ sources, sourceId, key: Date.now() });
  }, []);
  const ui = useMemo(() => ({ t, langOpt, openViewer }), [t, langOpt, openViewer]);

  const latest = useRef({ onSend, question });
  latest.current = { onSend, question };
  const { scrollToBottom } = scroll;
  // stable identity: memoized messages must not re-render because the composer text changed
  const send = useCallback((text, suggestion) => {
    scrollToBottom('auto');
    latest.current.onSend(typeof text === 'string' ? text : latest.current.question, suggestion);
  }, [scrollToBottom]);
  const append = useCallback((text) => setQuestion((prev) => `${prev}${text}`), [setQuestion]);

  // Keep the reading position when older messages are prepended.
  const firstId = messages.length ? messages[0].id : null;
  const prevFirst = useRef({ id: firstId, height: 0 });
  useLayoutEffect(() => {
    const el = scroll.ref.current;
    if (!el) return;
    const prev = prevFirst.current;
    if (prev.id && firstId && prev.id !== firstId && history && history.prepending) {
      el.scrollTop += el.scrollHeight - prev.height;
    }
    prevFirst.current = { id: firstId, height: el.scrollHeight };
  });

  const showLong = Boolean(conversationInfo && conversationInfo.long && !dismissedLong[conversationId || '_']);

  return (
    <ChakraProvider resetCSS={false}>
      <ChatUiContext.Provider value={ui}>
        <div className="cv-root">
          <div
            className="main-div cv-scroll"
            ref={scroll.ref}
            onScroll={scroll.onScroll}
            role="log"
            aria-busy={busy}
            aria-live="off"
          >
            <div className="cv-scroll__inner">
              {history && history.hasMore && messages.length > 0 && (
                <div className="cv-load-older">
                  <button type="button" className="cv-btn cv-btn--ghost" onClick={history.loadOlder} disabled={history.loading}>
                    {history.loading ? t('Loading...') : t('Load earlier messages')}
                  </button>
                </div>
              )}
              {messages.length === 0 ? emptyState : (
                <MessageList
                  messages={messages}
                  busy={busy}
                  onSend={send}
                  onAppend={append}
                  onRetry={chat.retry}
                  onVote={onVote}
                  votes={votes}
                />
              )}
            </div>
            {overlay}
          </div>
          <div className="footer cv-footer">
            {scroll.showJump && messages.length > 0 && (
              <button type="button" className="cv-jump" onClick={() => scrollToBottom()} aria-label={t('Jump to latest')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
                {t('Latest')}
              </button>
            )}
            {showLong && (
              <LongChatBanner
                info={conversationInfo}
                onNewChat={() => onNewChat({ keepSelection: true })}
                onDismiss={() => setDismissedLong((d) => ({ ...d, [conversationId || '_']: true }))}
              />
            )}
            <div className="footer-controls">{controls}</div>
            <UploadTray uploads={uploads} />
            <Composer
              value={question}
              onChange={setQuestion}
              onSend={() => send(question)}
              onStop={chat.stop}
              busy={busy}
              onAttach={uploads.add}
            />
          </div>
          <LiveAnnouncer messages={messages} />
          <PageViewer request={viewer} onClose={() => setViewer(null)} />
        </div>
      </ChatUiContext.Provider>
    </ChakraProvider>
  );
}
